import React from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useEngeneNavigate } from '../../../hooks/navigate';
import { useGetDailyMatterAssignMessage } from '../../../hooks/queries/dailyMatter';
import useToastHook from '../../../hooks/ToastHook';
import { AssignMessageGetResult } from '../../../models/gen/assignMessageGetResult';
import NavigateGoBack from '../../../routes/GoBack';
import { useMasterContext } from '../../MasterProvider';
import Wireframe from '../../wireframe/Wireframe';

/**
 * 手配(受注)画面パラメータ
 */
export type AssignmentParams = {
  /**
   * 手配メッセージID
   */
  assignMessageId: string;
};

export type AssignmentContext = {
  isFetched?: boolean;
  isLoading?: boolean;
  assignMessage?: AssignMessageGetResult;
};

/**
 * 手配(受注)画面
 *
 * - URL: `/assignment/:assignMessageId`
 * - パラメータ
 *   - assignMessageId: 手配メッセージID
 * - クエリ: なし
 *
 * @returns
 */
const Assignment: React.FunctionComponent = () => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }
  const master = useMasterContext();

  const navigate = useEngeneNavigate();
  const { assignMessageId } = useParams<AssignmentParams>();
  if (TypeUtils.isNil(assignMessageId)) {
    return <NavigateGoBack />;
  }

  const { toast } = useToastHook();
  const { data, error, isError, isLoading, refetch, isFetched } =
    useGetDailyMatterAssignMessage({
      id: parseInt(assignMessageId),
    });

  React.useState(() => {
    if (!TypeUtils.isNilString(assignMessageId)) {
      refetch();
    }
  });

  React.useEffect(() => {
    if (isError) {
      toast(
        error instanceof AppError ? error : '手配(受注)情報の取得に失敗しました'
      );
      navigate();
    }
  }, [isError]);

  return (
    <Wireframe
      isLoaded={!isLoading}
      title={
        TypeUtils.isNil(master.role.se)
          ? ''
          : state.userInfo?.role_id.includes(master.role.se!.id)
          ? pageNames.ASSIGNMENT_SE
          : pageNames.ASSIGNMENT_OUTSOURCER
      }
    >
      <Outlet context={{ assignMessage: data, isLoading, isFetched }} />
    </Wireframe>
  );
};

export default Assignment;
